import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Radio } from "lucide-react";
import { api } from "../lib/api";
import { SessionSuggestionsSkeleton } from "../components/PageSkeletons";

// session.date can be date-only ("YYYY-MM-DD") or a full ISO datetime,
// same convention as EmployeeProgress.js's formatSessionDate.
const formatDate = (d) => {
  if (!d) return "";
  if (!d.includes("T")) return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d;
  return parsed.toLocaleString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function SessionSuggestions() {
  const { id, sessionId } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/employees/${id}/scores/${sessionId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [id, sessionId]);

  if (!data) return <SessionSuggestionsSkeleton />;

  const { employee, rating, audio_sessions } = data;
  const specificRating = rating.specific_rating || [];
  const coachingExamples = rating.coaching_examples || [];

  return (
    <div className="space-y-8 fade-up">
      <div>
        <Link to={`/console/employees/${id}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#111] transition-colors">
          <ArrowLeft className="w-4 h-4" /> {employee.name}
        </Link>
        <div className="mt-4">
          <h1 className="font-heading font-light text-3xl tracking-tight">{formatDate(rating.date)}</h1>
          <p className="text-sm text-neutral-500">{employee.position ? `${employee.position} · ` : ""}{employee.department}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg border border-[#111] bg-[#111] text-white p-5">
          <div className="font-heading text-2xl font-light">{rating.overall_score}</div>
          <div className="text-xs mt-1 text-neutral-400">Overall</div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-5">
          <div className="font-heading text-2xl font-light">{rating.session_count}</div>
          <div className="text-xs mt-1 text-neutral-500">Interactions</div>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg" data-testid="session-interactions">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-sm font-medium">That day's interactions</h2>
        </div>
        {audio_sessions.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-400 text-center">No interaction summaries recorded for this day.</div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {audio_sessions.map((s) => (
              <div key={s.session_id} className="px-6 py-4">
                <div className="flex items-center gap-1.5 text-xs font-mono-data text-neutral-400">
                  <Radio className="w-3 h-3" /> {formatDate(s.session_date)}
                </div>
                <p className="mt-1.5 text-sm text-neutral-700 leading-relaxed">{s.summary || "No summary available."}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-sm font-medium px-1 mb-3">Ratings</h2>
        {specificRating.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg px-6 py-8 text-sm text-neutral-400 text-center">
            No detailed ratings available for this day.
          </div>
        ) : (
          <div className="space-y-4">
            {specificRating.map((item) => (
              <div key={item.criterion} className="bg-white border border-neutral-200 rounded-lg">
                <div className="px-6 py-4 flex items-center justify-between">
                  <h3 className="text-sm font-medium">{item.criterion}</h3>
                  <span className="font-heading text-lg">{item.rating}</span>
                </div>
                <div className="px-6 pb-4">
                  {item.assessment ? (
                    <p className="text-sm text-neutral-600 leading-relaxed">{item.assessment}</p>
                  ) : (
                    <p className="text-sm text-neutral-400 italic">No explanation recorded for this rating.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg" data-testid="coaching-examples">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-sm font-medium">Coaching moments</h2>
        </div>
        {coachingExamples.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-400 text-center">No coaching moments available for this day.</div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {coachingExamples.map((ex, i) => {
              const isGood = ex.quality === "good";
              return (
                <div key={i} className="px-6 py-5">
                  <span className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 border ${
                    isGood ? "text-green-700 bg-green-50 border-green-200" : "text-amber-700 bg-amber-50 border-amber-200"
                  }`}>
                    {isGood ? <Check className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                    {isGood ? "Good example" : "Needs improvement"}
                  </span>
                  <p className="mt-3 font-serif-accent italic text-neutral-700 leading-relaxed">“{ex.said}”</p>
                  {!isGood && ex.corrected && (
                    <div className="mt-2">
                      <div className="text-[11px] uppercase tracking-wide font-mono-data text-amber-700">Try instead</div>
                      <p className="mt-1 font-serif-accent italic text-neutral-700 leading-relaxed">“{ex.corrected}”</p>
                    </div>
                  )}
                  <div className="mt-2">
                    <div className={`text-[11px] uppercase tracking-wide font-mono-data ${isGood ? "text-green-700" : "text-amber-700"}`}>
                      {isGood ? "Why this worked" : "Explanation"}
                    </div>
                    <p className="mt-1 text-sm text-neutral-800 leading-relaxed">{ex.note}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
